const RELOAD_KEY = "maps.appEntryReload.v1";
const failureContexts = new WeakMap();
const MODULE_LOAD_ERROR = /^(?:Failed to fetch dynamically imported module|Importing a module script failed\.?|error loading dynamically imported module|Unable to preload CSS for|Error loading .+\(SystemJS Error#3)/i;

export const isAppEntryLoadError = (error) =>
  MODULE_LOAD_ERROR.test(typeof error === "string" ? error : error?.message || "");

function assetUrl(value, browser) {
  try {
    const url = new URL(value, browser.location.href);
    // Only report public build assets, never page URLs, credentials or queries.
    if (!/^https?:$/.test(url.protocol) ||
        !/\/assets\/[\w.-]+\.(?:js|css)$/.test(url.pathname)) return null;
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

function assetDetails(error, resource, browser, startedAt) {
  let timings = [];
  try {
    timings = browser.performance?.getEntriesByType("resource") || [];
  } catch {
    // Resource timing is optional and may be unavailable in private browsers.
  }
  const message = typeof error === "string" ? error : error?.message || "";
  const urls = message.match(/https?:\/\/[^\s"'<>]+|\/(?:[\w.-]+\/)*assets\/[\w.-]+\.(?:js|css)[^\s"'<>]*/g) || [];
  const failures = timings.filter((timing) =>
    timing.startTime >= startedAt && timing.responseStatus >= 400);
  const failedAsset = [...urls, resource, ...failures.map((timing) => timing.name)]
    .map((url) => url && assetUrl(url, browser)).find(Boolean) || null;
  const timing = [...timings].reverse().find((item) =>
    item.startTime >= startedAt && failedAsset && assetUrl(item.name, browser) === failedAsset);
  return {
    failedAsset,
    // Zero means unavailable, not an HTTP response. Do not invent a status.
    responseStatus: timing?.responseStatus >= 100 ? timing.responseStatus : null,
  };
}

function claimAutomaticReload(browser) {
  if (browser.navigator.onLine === false) return "offline";
  try {
    if (browser.sessionStorage.getItem(RELOAD_KEY)) return "already-attempted";
    browser.sessionStorage.setItem(RELOAD_KEY, "1");
    if (browser.sessionStorage.getItem(RELOAD_KEY) !== "1") return "storage-unavailable";
    return "automatic-reload";
  } catch {
    // Without a durable marker a reload could repeat forever.
    return "storage-unavailable";
  }
}

export function reloadAppEntry(browser = window) {
  try {
    browser.sessionStorage.setItem(RELOAD_KEY, "1");
  } catch {
    // A deliberate manual reload must still work when browser storage is blocked.
  }
  browser.location.reload();
}

export function annotateAppEntryError(scope, error) {
  const context = error && typeof error === "object" && failureContexts.get(error);
  if (!context) return;
  scope.setTag("area", "app_entry");
  scope.setTag("app_entry", context.entry);
  scope.setContext("module_load", context);
}

// Only wrap the three initial lazy entries. Never reload an already mounted
// application in response to a global preload event: it may contain unsaved work.
export async function loadAppEntry(importEntry, {
  entry,
  browser = window,
  onAutomaticReload = () => {},
}) {
  const startedAt = browser.performance?.now?.() || 0;
  let failedResource;
  const onResourceError = (event) => {
    const target = event.target;
    if (target?.tagName === "SCRIPT" || target?.tagName === "LINK") {
      const url = assetUrl(target.src || target.href, browser);
      if (url) failedResource = url;
    }
  };
  browser.addEventListener("error", onResourceError, true);
  try {
    const module = await importEntry();
    try {
      browser.sessionStorage.removeItem(RELOAD_KEY);
    } catch {
      // Successful startup does not depend on storage access.
    }
    return module;
  } catch (error) {
    if (!isAppEntryLoadError(error)) throw error;
    const context = {
      entry,
      online: browser.navigator.onLine !== false,
      recovery: claimAutomaticReload(browser),
      ...assetDetails(error, failedResource, browser, startedAt),
    };
    if (error && typeof error === "object") failureContexts.set(error, context);
    if (context.recovery === "automatic-reload") {
      try {
        browser.location.reload();
      } catch {
        context.recovery = "reload-unavailable";
        throw error;
      }
      try {
        // The first attempt is a warning; persistent failures are still captured
        // as errors by the root Sentry boundary, with this diagnostic context.
        onAutomaticReload(error, context);
      } catch {
        // Observability must not prevent recovery.
      }
      // Keep the loading state until navigation replaces this document.
      return new Promise(() => {});
    }
    throw error;
  } finally {
    browser.removeEventListener("error", onResourceError, true);
  }
}
